<template>
  <div class="mc-livetrack--qa clearfix" :class="{ 'detail-opened': selectedPointIdx !== null }">
    <template v-if="!isExpired">
      <Sidebar :loading="loadingDetailFO" :currentDateTime="currentDateTime" />
      <Portal to="mobile-detail-point" class="mc-livetrack--qa-detail-point" :class="{ 'opened': selectedPointIdx !== null }" :disabled="!isMobileView">
        <TaskDetailPointSection
          v-show="selectedPointIdx !== null"
          :fleetTaskInstantId="detailData.detail?.id"
          isLiveTrack
        />
      </Portal>
      <div id="livetrack-map" :class="{ 'mc-livetrack--qa-map': true }">
        <SegmentedTabs
          v-if="detailData.detail?.status.raw_type === 'STARTED'"
          class="__segmented-tabs"
          :items="tabMappings"
          :activeTab="activeTab"
          :isLoading="loadingDetailFO"
          @update:activeTab="handleActiveTab"
        />
        <MapPreview
          style="position: relative"
          width="100%"
          isLiveTrack
          :loadingOverlay="loadingDetailFO"
          :showAddressMarkerLabel="activeTab == 'vehicle'"
          :isMobileView="isMobileView"
        />
      </div>
    </template>

    <div v-else class="mc-livetrack-expired">
      <img :src="require('@/assets/images/temp-tracking/expired-link.svg')" width="300px" />
      <div class="title">
        {{ $t('fleetTaskInstant.title.liveTrackExpired') }}
      </div>
      <div class="subtitle">
        {{ $t('fleetTaskInstant.subtitle.liveTrackExpired') }}
      </div>
    </div>
  </div>
</template>
<script>
import appConfig from '@/app.config'

import MapPreview from './sections/MapPreview'
import Sidebar from './components/LiveTrack/Sidebar/index'
import SegmentedTabs from './components/SegmentedTabs'
import TaskDetailPointSection from './sections/TaskDetailSection/TaskDetailPointSection'

import { mapGetters, mapState } from 'vuex'
import { formatIndexDate } from '@/helpers/tzDate'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { EventBus } from './lib/event-bus'

import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'
import { SHOW_VEHICLE } from '@/services/api/fleetTaskInstant'
import { computed } from 'vue'

export default {
  page: {
    title: 'Pelacakan FO',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Sidebar,
    MapPreview,
    SegmentedTabs,
    TaskDetailPointSection,
  },
  mixins: [Helper, MixPanel],
  provide() {
    return {
      isMobileView: computed(() => this.isMobileView),
      isDriverView: computed(() => this.isDriverView)
    }
  },
  data() {
    return {
      data: this.$route.params.data || null,
      uid: null,
      token: null,

      loadingDetailFO: true,
      toggleSidebar: true,
      isExpired: false,

      detailData: {},
      detailVehicle: {},

      driverPhoneNo: null,
      lastPosition: null,
      interval: null,

      currentDateTime: this.getCurrentDateTime(),

      innerWidth: 0,
    }
  },
  computed: {
    ...mapGetters({
      getAsset: 'asset/getAsset',
    }),
    ...mapState('fleetTaskInstant',[
      'selectedPointIdx',
      'activeTab',
    ]),
    isMobileView() {
      return this.innerWidth <= 890
    },
    tabMappings() {
      return [
        {
          key: 'vehicle',
          name: this.$t('fleetTaskInstant.tab.vehicle'),
          icon: 'icon-fill_car',
        },
        {
          key: 'route',
          name: this.$t('fleetTaskInstant.tab.route'),
          icon: 'icon-outline_calculate',
        },
      ]
    },
    isDriverView() {
      return this.token.endsWith('type=driver')
    },
  },
  created() {
    this.actionAfterLoginIdentity()
    this.innerWidth = window.innerWidth
    window.addEventListener('resize', this.handleResize)
  },
  async mounted() {
    await this.getData()
    await this.getDetail()
    EventBus.$emit('live-track-data-ready')

    if (this.detailData.detail.status.raw_type !== 'ENDED') {
      this.handleSetInterval()
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    formatIndexDate,

    handleResize() {
      this.innerWidth = window.innerWidth
    },

    getCurrentDateTime() {
      const now = new Date()
      const isoDateTime = now.toISOString()
      return isoDateTime
    },

    getData() {
      const decodedData = atob(this.data)
      const [uidPart, tokenPart] = decodedData.split('?token=')
      this.token = tokenPart

      let newUid = btoa(uidPart) + '&&' + this.getCompanyId()
      if (this.isDriverView) {
        newUid += '&&driver'
      }

      this.uid = btoa(newUid)
    },
    async getDetail(type = 'init') {
      const res = await FleetTaskInstant.track(this.uid)

      if (res.errors && res.status === 404) {
        this.isExpired = true
        return
      }

      this.detailData = res.data
      const vehicleId = this.detailData.detail.vehicle.id

      if (this.detailData?.detail?.status?.raw_type === 'ENDED') {
        this.$store.dispatch('fleetTaskInstant/setActiveTab', null)
      }
      try {
        const vehicleStatusRes = await this.$apollo.query({
          query: SHOW_VEHICLE,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              vehicleId: parseInt(vehicleId),
            },
          },
        })

        if (!vehicleStatusRes.data.vehicleStatus.isSuccessful) {
          const data = {
            hasButton: false,
          }
          localStorage.setItem('404', JSON.stringify(data))
          this.$router.push({
            name: '404',
          })
        }

        this.lastPosition = vehicleStatusRes.data.vehicleStatus.vehicleStatus

        await this.$store.dispatch('fleetTaskInstant/setLiveTrackResponse', {
          liveTrack: res.data,
          vehicle: vehicleStatusRes.data.vehicleStatus,
          driver: this.detailData.detail.driver,
        })
        await this.$nextTick()
        EventBus.$emit('success-fetch-live-track-data', type)

        this.track('Track Fleet Task')
      } catch {
        this.$router.push({
          name: '404',
        })
      } finally {
        this.loadingDetailFO = false
      }
    },
    handleSetInterval() {
      clearInterval(this.interval)

      this.interval = setInterval(async () => {
        await this.getDetail('update')
        if (this.detailData.detail?.status.raw_type === 'ENDED') {
          this.clearTimeInterval()
        }
      }, 10000)
    },
    clearTimeInterval() {
      clearInterval(this.interval)
      this.interval = null
    },

    handleActiveTab(event) {
      this.$store.dispatch('fleetTaskInstant/setActiveTab', event)

      if (this.selectedPointIdx === null) {
        EventBus.$emit('handle-focus-map-view')
      }
    }
  },
}
</script>

<style lang="scss">
@import '@/design/custom/pages/_fleettaskinstant.scss';
</style>